import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './LandingPageHeroTextLoop.css';

interface TextLoopProps {
  readonly children: React.ReactNode;
  readonly interval?: number;
  readonly className?: string;
}

const ANIMATION_DURATION_MS: number = 520;

const TextLoop = ({
  children,
  interval = 2500,
  className = '',
}: TextLoopProps): React.JSX.Element => {
  const items: readonly React.ReactNode[] = React.Children.toArray(children);
  const [index, setIndex] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const itemRef = useRef<HTMLDivElement | null>(null);
  const loopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect((): void => {
    const nextHeight: number = itemRef.current?.offsetHeight ?? 0;

    if (nextHeight > 0) {
      setContainerHeight(nextHeight);
    }
  }, [index, items]);

  useEffect((): (() => void) | void => {
    if (items.length <= 1) {
      return undefined;
    }

    loopTimeoutRef.current = setTimeout((): void => {
      setIsAnimating(true);
    }, interval);

    return (): void => {
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
      }
    };
  }, [index, interval, items.length]);

  useEffect((): (() => void) | void => {
    if (!isAnimating || items.length <= 1) {
      return undefined;
    }

    animationTimeoutRef.current = setTimeout((): void => {
      setIndex((previousIndex: number): number => (previousIndex + 1) % items.length);
      setIsAnimating(false);
    }, ANIMATION_DURATION_MS);

    return (): void => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [isAnimating, items.length]);

  if (items.length === 0) {
    return <></>;
  }

  const resolvedClassName: string = className.trim().length > 0 ? `textloop-container ${className}` : 'textloop-container';
  const containerStyle: React.CSSProperties = containerHeight > 0 ? { minHeight: `${containerHeight}px` } : {};

  return (
    <div className={resolvedClassName} style={containerStyle}>
      <div
        ref={itemRef}
        className={`textloop-item ${isAnimating ? 'exit' : 'enter'}`}
        key={index}
      >
        {items[index]}
      </div>
    </div>
  );
};

export default TextLoop;